import {zodResolver} from '@hookform/resolvers/zod';
import {Button as MantineButton, PasswordInput, PinInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Container} from 'src/design/atoms/Container';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {Controller, useForm} from 'react-hook-form';
import {Link} from 'react-router-dom';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {PasswordStrength} from 'src/design/molecules/PasswordStrength';

import {
  IconAlertCircle,
  IconCheck,
  IconChevronRightPipe,
} from '@tabler/icons-react';
import {Button} from 'src/design/atoms/Button';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {
  PasswordResetState,
  confirmUserPassword,
} from 'src/redux/slices/authSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {z} from 'zod';

const schema = z
  .object({
    identifier: z.string(),
    code: z
      .string()
      .length(6, {message: 'Confirmation code should be 6 characters.'}),

    password: z.string().min(8),
    confirmPassword: z.string().min(8),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'], // path of error
  })
  .superRefine(({password}, checkPassComplexity) => {
    const containsUppercase = (ch: string) => /[A-Z]/.test(ch);
    const containsLowercase = (ch: string) => /[a-z]/.test(ch);
    const containsSpecialChar = (ch: string) =>
      /[`!@#$%^&*()_\-+=[\]{};':"\\|,.<>/?~ ]/.test(ch);
    let countOfUpperCase = 0,
      countOfLowerCase = 0,
      countOfNumbers = 0,
      countOfSpecialChar = 0;
    for (let i = 0; i < password.length; i++) {
      const ch = password.charAt(i);
      if (!isNaN(+ch)) countOfNumbers++;
      else if (containsUppercase(ch)) countOfUpperCase++;
      else if (containsLowercase(ch)) countOfLowerCase++;
      else if (containsSpecialChar(ch)) countOfSpecialChar++;
    }
    if (
      countOfLowerCase < 1 ||
      countOfUpperCase < 1 ||
      countOfSpecialChar < 1 ||
      countOfNumbers < 1
    ) {
      checkPassComplexity.addIssue({
        code: 'custom',
        message: 'Password does not meet complexity requirements.',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

const ResetPasswordPage = () => {
  useSetDocumentTitle('Reset Password');

  const {username} = useAppSelector(state => state.auth);

  const dispatch = useAppDispatch();

  const {isLoading, isError, message, passwordResetStep} = useAppSelector(
    state => state.auth
  );

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      identifier: username ?? '',
      code: '',
      password: '',
      confirmPassword: '',
    },
    resolver: zodResolver(schema),
  });

  function handleSubmit(values: FormValues): void {
    void dispatch(
      confirmUserPassword({
        username: values.identifier,
        confirmationCode: values.code,
        newPassword: values.confirmPassword,
      })
    );
  }

  return (
    <Container size={450} my={40}>
      <Stack>
        <Title order={2} ta="center">
          Reset your password
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Enter your email to get a reset code
        </Text>

        <Paper withBorder shadow="md" p={30} radius="md">
          {passwordResetStep === PasswordResetState.NotStarted &&
            !isError &&
            !isLoading && (
              <Alert
                color="yellow"
                title="Oops! You're not supposed to be here."
                icon={<IconAlertCircle />}>
                <Stack>
                  <Text size="sm">
                    It looks like you are trying to reset your password but have
                    refreshed your browser or directly accessed this page.
                  </Text>
                  <Text size="sm">
                    Use the 'Back to login' button to return to the login page.
                    You can use the Forgot Password option on the login page to
                    reset your password.
                  </Text>
                </Stack>
              </Alert>
            )}
          {passwordResetStep === PasswordResetState.NotStarted &&
            isError &&
            message !== undefined && (
              <Alert
                color="red"
                title="Issue sending code"
                icon={<IconAlertCircle />}>
                <Stack>
                  <Text size="xs">
                    We couldn't send your reset code. Here's what might be
                    wrong:
                  </Text>
                  <List size="xs" spacing="xs">
                    <List.Item>
                      The email address might be incorrect or not registered
                    </List.Item>
                    <List.Item>
                      You've tried resetting too many times - please wait a few
                      minutes
                    </List.Item>
                    <List.Item>
                      Your account might be locked for security
                    </List.Item>
                  </List>
                  <Text size="xs">
                    Still having trouble? Contact support and show them the
                    error message below.
                  </Text>
                  <Text c="dimmed" size="xs">
                    Error: {message}
                  </Text>
                </Stack>
              </Alert>
            )}
          {passwordResetStep === PasswordResetState.PendingConfirmationCode && (
            <>
              <form
                onSubmit={handleFormSubmit(handleSubmit)}
                autoComplete="off">
                <Stack gap="lg">
                  <Text c="dimmed" size="sm">
                    {`A confirmation code was sent to your email. Please enter the confirmation code below to reset your password.`}
                  </Text>

                  <Group justify="center" py="lg">
                    <Controller
                      name="code"
                      control={control}
                      render={({field, fieldState}) => (
                        <PinInput
                          length={6}
                          {...field}
                          error={!!fieldState.error}
                          autoFocus
                        />
                      )}
                    />
                  </Group>

                  <Controller
                    name="password"
                    control={control}
                    render={({field, fieldState}) => (
                      <PasswordInput
                        label="Password"
                        placeholder="Password"
                        {...field}
                        error={fieldState.error?.message}
                        autoComplete="new-password"
                      />
                    )}
                  />
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({field, fieldState}) => (
                      <PasswordInput
                        label="Confirm Password"
                        placeholder="Confirm Password"
                        {...field}
                        error={fieldState.error?.message}
                        autoComplete="new-password"
                      />
                    )}
                  />

                  <Box>
                    <PasswordStrength password={watch('password')} />
                  </Box>

                  <Button fullWidth type="submit">
                    Confirm
                  </Button>
                  <Alert
                    color="yellow"
                    title="Remember your password!"
                    icon={<IconAlertCircle />}>
                    <List size="xs" spacing="xs">
                      <List.Item>
                        Passwords are case sensitive and must be entered exactly
                        during login.
                      </List.Item>
                      <List.Item>
                        Administrators can never see your password if you forget
                        it.
                      </List.Item>
                      <List.Item>
                        You can always reset your password again.
                      </List.Item>
                      <List.Item>
                        If your device supports it, you can save your password
                        to your browser to avoid having to remember it.
                      </List.Item>
                    </List>
                  </Alert>
                </Stack>
              </form>
              <ServerErrorAlert message={message} />
            </>
          )}
          {passwordResetStep === PasswordResetState.Done && (
            <Stack gap="lg">
              <Alert color="green">
                <List size="sm" spacing="xs">
                  <List.Item
                    icon={<IconCheck color="var(--mantine-color-green-6)" />}>
                    Your password has been reset!
                  </List.Item>
                  <List.Item
                    icon={
                      <IconChevronRightPipe color="var(--mantine-color-blue-6)" />
                    }>
                    You may now return to the login screen and login with your
                    new password.
                  </List.Item>
                </List>
              </Alert>

              <MantineButton
                component={Link}
                fullWidth
                to={PathUtility.Login()}>
                Back to login
              </MantineButton>
            </Stack>
          )}
        </Paper>
        <Anchor c="dimmed" size="sm" component={Link} to={PathUtility.Login()}>
          ← Back to login
        </Anchor>
      </Stack>
    </Container>
  );
};

// Register the route with the new system
routeRegistry.registerPageRoute({
  backNavigation: 'transient',
  path: PathUtility.ResetPassword(),
  element: <ResetPasswordPage />,
  isPrivate: false,
});

export {ResetPasswordPage};
